import { AmmoProductFormValues, CartridgeSpecFormValues } from '../../app/models/ammo'

export const AMMO_SUBMIT_FORM_STORAGE_KEY = 'gunslop_ammo_submit_form_state'

/** @deprecated Use activeStep — kept for legacy session payloads */
export type AmmoSubmitTab = 'spec' | 'product'

export type AmmoSubmitStep = 0 | 1

export type AmmoSubmitPersistedState = {
  v: 1
  activeStep: AmmoSubmitStep
  specForm: CartridgeSpecFormValues
  ammoForm: AmmoProductFormValues
  matchedCartridgeSpecId?: string | null
  /** @deprecated migrated to activeStep on read */
  activeTab?: AmmoSubmitTab
}

export const AMMO_SUBMIT_RETURN_PATH = '/ammo/submit'
export const AMMO_SUBMIT_RETURN_TITLE = 'Ammo Submit'

export function tabToStep (tab: AmmoSubmitTab): AmmoSubmitStep {
  return tab === 'product' ? 1 : 0
}

export function normalizePersistedState (raw: Record<string, unknown>): AmmoSubmitPersistedState | null {
  if (raw?.v !== 1 || !raw.specForm || !raw.ammoForm) return null
  const activeStep =
    typeof raw.activeStep === 'number'
      ? (raw.activeStep === 1 ? 1 : 0)
      : raw.activeTab === 'product'
        ? 1
        : 0
  return {
    v: 1,
    activeStep,
    specForm: raw.specForm as CartridgeSpecFormValues,
    ammoForm: raw.ammoForm as AmmoProductFormValues,
    matchedCartridgeSpecId:
      typeof raw.matchedCartridgeSpecId === 'string' ? raw.matchedCartridgeSpecId : null
  }
}

export function saveAmmoSubmitFormState (state: Omit<AmmoSubmitPersistedState, 'v' | 'activeTab'>): void {
  const payload: AmmoSubmitPersistedState = {
    v: 1,
    activeStep: state.activeStep,
    specForm: state.specForm,
    ammoForm: state.ammoForm,
    matchedCartridgeSpecId: state.matchedCartridgeSpecId ?? null
  }
  sessionStorage.setItem(AMMO_SUBMIT_FORM_STORAGE_KEY, JSON.stringify(payload))
}

export function peekAmmoSubmitFormState (): AmmoSubmitPersistedState | null {
  try {
    const raw = sessionStorage.getItem(AMMO_SUBMIT_FORM_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return normalizePersistedState(parsed)
  } catch {
    return null
  }
}

/** Read persisted submit form state and remove it (one-time restore on return). */
export function consumeAmmoSubmitFormState (): AmmoSubmitPersistedState | null {
  const state = peekAmmoSubmitFormState()
  if (state) {
    sessionStorage.removeItem(AMMO_SUBMIT_FORM_STORAGE_KEY)
  }
  return state
}

export function clearAmmoSubmitFormState (): void {
  sessionStorage.removeItem(AMMO_SUBMIT_FORM_STORAGE_KEY)
}

/** Optional URL-safe snapshot for returnTo links (sessionStorage remains primary). */
export function encodeAmmoSubmitStateParam (
  state: Omit<AmmoSubmitPersistedState, 'v' | 'activeTab'>
): string {
  return encodeURIComponent(JSON.stringify({ v: 1, ...state }))
}

export function decodeAmmoSubmitStateParam (encoded: string | null | undefined): AmmoSubmitPersistedState | null {
  if (!encoded) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(encoded)) as Record<string, unknown>
    return normalizePersistedState(parsed)
  } catch {
    return null
  }
}

export function buildAmmoHelpUrl (
  field: string,
  returnTo: string,
  state?: Omit<AmmoSubmitPersistedState, 'v' | 'activeTab'>
): string {
  const params = new URLSearchParams({ returnTo })
  if (state) {
    params.set('state', encodeAmmoSubmitStateParam(state))
  }
  return `/help/ammo/${encodeURIComponent(field)}?${params.toString()}`
}
